import { useCallback, useRef } from 'react';
import { useSoundSettings } from '../context/SoundSettingsContext';

const normalizeVolume = (volume) => Math.max(0, Math.min(100, volume)) / 100;

// Prevents double-tap audio spam while staying below the typical human double-click
// detection threshold (~100ms). Value chosen empirically.
const THROTTLE_MS = 80;

// Cache the prefers-reduced-motion query at module load time and keep it in sync
// via a listener so we don't query the DOM on every sound playback.
let _reducedMotion = false;
if (typeof window !== 'undefined' && window.matchMedia) {
  const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
  _reducedMotion = mql.matches;
  mql.addEventListener('change', (e) => { _reducedMotion = e.matches; });
}

function useManagedSound(filePath, options = {}) {
  const { enabled, volume } = useSoundSettings();
  const audioRef = useRef(null);
  const lastPlayedRef = useRef(0);
  const { fixedVolume, volumeMultiplier = 1, preventOverlap = false, throttleMs = THROTTLE_MS } = options;

  return useCallback(() => {
    if (!enabled) return;
    if (_reducedMotion) return;
    const now = Date.now();
    if (now - lastPlayedRef.current < throttleMs) return;
    lastPlayedRef.current = now;
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(filePath);
        audioRef.current.preload = 'metadata';
      }
      if (preventOverlap && !audioRef.current.paused) return;
      if (!audioRef.current.paused) {
        audioRef.current.pause();
      }
      audioRef.current.currentTime = 0;
      audioRef.current.volume = fixedVolume ?? (normalizeVolume(volume) * volumeMultiplier);
      audioRef.current.play().catch(() => {});
    } catch (_) {}
  }, [enabled, filePath, fixedVolume, preventOverlap, throttleMs, volume, volumeMultiplier]);
}

export function useClickSound() {
  const playClick = useManagedSound('/sounds/click.wav', { fixedVolume: 0.4 });

  return { playClick };
}

export function useSirenSound() {
  const playSiren = useManagedSound('/sounds/siren.wav', {
    volumeMultiplier: 0.8,
    preventOverlap: true,
  });

  return { playSiren };
}

export function useChimeAlertSound() {
  const playChime = useManagedSound('/sounds/chime-alert.wav');

  return { playChime };
}

export function useSuccessConfirmSound() {
  const playSuccessConfirm = useManagedSound('/sounds/success-confirm.wav');

  return { playSuccessConfirm };
}

export function useScanFoundSound() {
  const playScanFound = useManagedSound('/sounds/scan-found.wav');

  return { playScanFound };
}

export function useScanEmptySound() {
  const playScanEmpty = useManagedSound('/sounds/scan-empty.wav');

  return { playScanEmpty };
}

export function useMapOpenSound() {
  const playMapOpen = useManagedSound('/sounds/map-open.wav');

  return { playMapOpen };
}

export function useLoginSuccessSound() {
  const playLoginSuccess = useManagedSound('/sounds/login-success.wav');

  return { playLoginSuccess };
}

export function useLogoutSound() {
  const playLogout = useManagedSound('/sounds/logout.wav');

  return { playLogout };
}

export function useSuccessSound() {
  const playSuccess = useManagedSound('/sounds/success.wav');

  return { playSuccess };
}

export function useErrorSound() {
  const playError = useManagedSound('/sounds/error.wav');

  return { playError };
}

export function useSwooshSound() {
  const playSwoosh = useManagedSound('/sounds/swoosh.wav', { fixedVolume: 0.22 });

  return { playSwoosh };
}

export function useBlipSound() {
  const playBlip = useManagedSound('/sounds/blip.wav', { fixedVolume: 0.25 });

  return { playBlip };
}

export function usePingSound() {
  const playPing = useManagedSound('/sounds/ping.wav', { fixedVolume: 0.25 });

  return { playPing };
}

export function useSoftTapSound() {
  const playSoftTap = useManagedSound('/sounds/soft-tap.wav', { fixedVolume: 0.20 });

  return { playSoftTap };
}

export function useTickSound() {
  const playTick = useManagedSound('/sounds/tick.wav', { fixedVolume: 0.22 });

  return { playTick };
}

export function useSOSSound() {
  const playSOS = useManagedSound('/sounds/sos.wav', { fixedVolume: 0.25 });

  return { playSOS };
}

export function useFileOpenSound() {
  const playFileOpen = useManagedSound('/sounds/file-open.wav', { fixedVolume: 0.20 });

  return { playFileOpen };
}

export function useNavClickSound() {
  const playNavClick = useManagedSound('/sounds/nav-click.wav', { fixedVolume: 0.22 });

  return { playNavClick };
}
